import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Download, Copy, CheckCircle2, FileText, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../../lib/LanguageContext';

export default function PageRIB() {
  const { t } = useLanguage();
  const [me, setMe] = useState(null);
  const [copied, setCopied] = useState('');

  useEffect(() => { (async () => {
    try { const r = await api.get('/client/me'); setMe(r.data.user); }
    catch { toast.error(t('common.loadError')); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  })(); }, []);

  if (!me) return <div className="text-slate-500">{t('common.loading')}</div>;

  const ibanFormatted = (me.iban || '').replace(/(.{4})/g, '$1 ').trim();

  const downloadRib = async () => {
    try {
      const res = await api.get('/client/rib.pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `RIB_BFI_${me.nom}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t('rib.downloaded'));
    } catch (e) { toast.error(e?.response?.data?.detail || t('rib.noIban')); }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 1500);
  };

  if (!me.iban) {
    return (
      <div className="space-y-5">
        <h1 className="text-2xl font-bold">{t('rib.title')}</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <FileText className="w-12 h-12 mx-auto text-amber-600 mb-3"/>
          <h2 className="font-semibold text-lg">{t('rib.ibanPending')}</h2>
          <p className="text-slate-600 text-sm mt-2">{t('rib.ibanPendingDesc')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t('rib.title')}</h1>
          <p className="text-slate-500 text-sm">{t('rib.subtitle')}</p>
        </div>
        <Button onClick={downloadRib} className="bg-blue-700" data-testid="btn-download-rib-page">
          <Download className="w-4 h-4 mr-2"/> {t('rib.downloadPdf')}
        </Button>
      </div>

      {/* RIB visuel */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="bg-gradient-to-br from-blue-900 to-blue-700 p-6 text-white">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-widest text-blue-200">{t('rib.officialTitle')}</div>
              <div className="text-2xl font-bold mt-1">BFI Groupe</div>
            </div>
            <Badge className="bg-white text-blue-900">{t('rib.official')}</Badge>
          </div>
        </div>
        <div className="p-6 space-y-4">
          <Field label={t('rib.accountHolder')} value={`${me.civilite || ''} ${me.prenom || ''} ${me.nom || ''}`.trim()} onCopy={copyToClipboard} copied={copied}/>
          <Field label={t('transfer.iban')} value={ibanFormatted} mono onCopy={copyToClipboard} copied={copied}/>
          <Field label={t('rib.bicSwift')} value={me.bic || '—'} mono onCopy={copyToClipboard} copied={copied}/>
          <Field label={t('rib.bankName')} value="BFI Groupe — RTE CHABAN, 79180 CHAURAY, France" onCopy={copyToClipboard} copied={copied}/>
        </div>
      </div>

      {/* Notes utiles */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-blue-900 font-semibold"><Mail className="w-4 h-4"/> {t('rib.receiveTransfers')}</div>
          <p className="text-sm text-slate-700 mt-2">{t('rib.receiveTransfersDesc')}</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold"><CheckCircle2 className="w-4 h-4"/> {t('rib.bankCompliance')}</div>
          <p className="text-sm text-slate-700 mt-2">{t('rib.bankComplianceDesc')}</p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, mono = false, onCopy, copied }) {
  const isCopied = copied === label;
  return (
    <div className="flex items-start justify-between gap-3 pb-3 border-b border-slate-100 last:border-0">
      <div className="flex-1 min-w-0">
        <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
        <div className={`mt-1 text-sm ${mono ? 'font-mono' : 'font-medium'} text-slate-900 break-all`}>{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onCopy(value, label)} className="shrink-0" data-testid={`btn-copy-${label.toLowerCase()}`}>
        {isCopied ? <CheckCircle2 className="w-4 h-4 text-emerald-600"/> : <Copy className="w-4 h-4 text-slate-500"/>}
      </Button>
    </div>
  );
}
